import React from 'react';
import { useNavigate } from 'react-router-dom';
import PublicPageLayout from '../../components/layout/PublicPageLayout';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import SchemaMarkup from '../../components/SchemaMarkup';

const { FiShield, FiGlobe, FiLock, FiFileText, FiCheck, FiArrowRight } = FiIcons;

const CompliancePage = () => {
  const navigate = useNavigate();

  const faqData = [
    { q: "Er systemet godkjent av Datatilsynet?", a: "Datatilsynet 'godkjenner' ikke enkeltsystemer, men vi følger alle retningslinjer og krav satt av Datatilsynet for informasjonssikkerhet og behandling av personopplysninger i varslingssaker." },
    { q: "Hvor lenge lagres data?", a: "Data slettes når de ikke lenger er nødvendige for saksbehandlingen. Hvis et varsel viser seg å være grunnløst, slettes personopplysningene umiddelbart. Arkiverte saker slettes automatisk etter 5 år i henhold til regnskaps- og arkivlovgivning der det er relevant." }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": f.a
      }
    }))
  };

  return (
    <PublicPageLayout 
      title="Regelverk og Etterlevelse" 
      subtitle="Vi sørger for at din bedrift oppfyller alle lovkrav knyttet til varsling og personvern."
      activePage="/regelverk-og-etterlevelse"
    >
      <SchemaMarkup data={faqSchema} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">

        {/* Intro Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center text-red-600 mb-6">
              <SafeIcon icon={FiFileText} className="text-2xl" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">Arbeidsmiljøloven</h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              Systemet er bygget i henhold til kapittel 2 A i arbeidsmiljøloven. Vi sikrer kravet til skriftlige rutiner, forsvarlig saksbehandling og vern mot gjengjeldelse.
            </p>
          </div>
          <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-6">
              <SafeIcon icon={FiGlobe} className="text-2xl" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">EU Varslingsdirektiv</h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              For bedrifter med over 50 ansatte oppfyller vi kravene i EU Whistleblowing Directive (2019/1937), inkludert strenge frister for bekreftelse (7 dager) og tilbakemelding (3 måneder).
            </p>
          </div>
        </div>

        {/* GDPR Section & Documents */}
        <section className="bg-slate-50 rounded-3xl p-8 md:p-12 border border-slate-100">
          <div className="flex flex-col md:flex-row gap-8 items-start">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                <SafeIcon icon={FiLock} className="text-green-600" />
                Personvern og GDPR
              </h2>
              <div className="prose prose-slate text-slate-600">
                <p className="mb-4">
                  Behandling av varslingssaker innebærer behandling av sensitive personopplysninger. HMSVarsling fungerer som databehandler og ivaretar sikkerheten gjennom:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <SafeIcon icon={FiCheck} className="text-green-500 mt-1 flex-shrink-0" />
                    <span>Innebygd sletting av data når formålet er oppnådd</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <SafeIcon icon={FiCheck} className="text-green-500 mt-1 flex-shrink-0" />
                    <span>Tilgangsstyring (kun autorisert personell)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <SafeIcon icon={FiCheck} className="text-green-500 mt-1 flex-shrink-0" />
                    <span>Kryptert lagring og overføring (SSL/TLS)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <SafeIcon icon={FiCheck} className="text-green-500 mt-1 flex-shrink-0" />
                    <span>Servere plassert innenfor EU/EØS (Tyskland)</span>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* FIXED LINKS: Using navigate instead of href to prevent white screen */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 w-full md:w-72">
              <h4 className="font-bold text-slate-900 mb-4 text-sm uppercase tracking-wider">Dokumentasjon</h4>
              <ul className="space-y-4 text-sm text-blue-600 font-medium">
                <li>
                  <button onClick={() => navigate('/privacy')} className="hover:underline flex items-center gap-2 text-left w-full hover:bg-blue-50 p-2 rounded transition-colors">
                    <SafeIcon icon={FiFileText} /> Personvernerklæring
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/terms')} className="hover:underline flex items-center gap-2 text-left w-full hover:bg-blue-50 p-2 rounded transition-colors">
                    <SafeIcon icon={FiFileText} /> Databehandleravtale
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/sikkerhet')} className="hover:underline flex items-center gap-2 text-left w-full hover:bg-blue-50 p-2 rounded transition-colors text-green-700">
                    <SafeIcon icon={FiShield} /> Sikkerhetsrevisjon
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* FAQ Compliance */}
        <section>
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Ofte stilte spørsmål om compliance</h2>
          <div className="space-y-4">
            <details className="group bg-white border border-gray-200 rounded-xl p-4 cursor-pointer">
              <summary className="font-medium text-slate-900 flex justify-between items-center">
                <span>Er systemet godkjent av Datatilsynet?</span>
                <span className="text-slate-400 group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <p className="text-slate-600 mt-4 text-sm leading-relaxed">
                Datatilsynet "godkjenner" ikke enkeltsystemer, men vi følger alle retningslinjer og krav satt av Datatilsynet for informasjonssikkerhet og behandling av personopplysninger i varslingssaker.
              </p>
            </details>
            <details className="group bg-white border border-gray-200 rounded-xl p-4 cursor-pointer">
              <summary className="font-medium text-slate-900 flex justify-between items-center">
                <span>Hvor lenge lagres data?</span>
                <span className="text-slate-400 group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <p className="text-slate-600 mt-4 text-sm leading-relaxed">
                Data slettes når de ikke lenger er nødvendige for saksbehandlingen. Hvis et varsel viser seg å være grunnløst, slettes personopplysningene umiddelbart. Arkiverte saker slettes automatisk etter 5 år i henhold til regnskaps- og arkivlovgivning der det er relevant.
              </p>
            </details>
          </div>
        </section>

      </div>
    </PublicPageLayout>
  );
};

export default CompliancePage;